import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, Calendar, CheckCircle, Clock, Loader2 } from 'lucide-react';
import api from '../../services/api';

const VendorStatistics = () => {
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState(null);
  const [period, setPeriod] = useState('month'); // day, week, month

  useEffect(() => {
    fetchStatistics();
  }, [period]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/vendors/statistics?period=${period}`);
      setStatsData(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#00FFFF] animate-spin" />
      </div>
    );
  }

  if (!statsData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#B8B8BA]">Erreur de chargement des statistiques</div>
      </div>
    );
  }

  // Couleurs pour les graphiques
  const COLORS = {
    available: '#00FF88',
    pending: '#FFB800',
    withdrawn: '#00FFFF',
    total: '#B8B8BA'
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2">📊 Statistiques</h1>
          <p className="text-[#B8B8BA]">Analyse de vos performances et gains</p>
        </motion.div>

        {/* Sélecteur de période */}
        <div className="flex gap-4 mb-8">
          {['day', 'week', 'month'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-6 py-2 rounded-lg transition-colors ${
                period === p
                  ? 'bg-[#00FFFF] text-[#0A0A0B] font-semibold'
                  : 'bg-[#1A1A1D] text-[#B8B8BA] hover:bg-[#222226]'
              }`}
            >
              {p === 'day' ? 'Jour' : p === 'week' ? 'Semaine' : 'Mois'}
            </button>
          ))}
        </div>

        {/* KPIs en haut */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-sm">Gains sur la période</span>
              <DollarSign className="h-5 w-5 text-[#00FF88]" />
            </div>
            <div className="text-3xl font-bold text-[#00FF88]">
              {statsData.period_earnings?.toFixed(2) || '0.00'}€
            </div>
          </div>

          <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-sm">Taux de validation</span>
              <CheckCircle className="h-5 w-5 text-[#00FFFF]" />
            </div>
            <div className="text-3xl font-bold text-[#00FFFF]">
              {statsData.validation_rate || '0'}%
            </div>
          </div>

          <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-sm">Temps moyen validation</span>
              <Clock className="h-5 w-5 text-[#FFB800]" />
            </div>
            <div className="text-3xl font-bold text-[#FFB800]">
              {statsData.avg_validation_time || '0'}h
            </div>
          </div>

          <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-sm">Projection palier</span>
              <TrendingUp className="h-5 w-5 text-[#B8B8BA]" />
            </div>
            <div className="text-xl font-bold text-white">
              {statsData.current_tier} → {statsData.next_tier || 'Max'}
            </div>
          </div>
        </div>

        {/* Graphique: Répartition des soldes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border">
            <h2 className="text-2xl font-bold text-white mb-6">Répartition des soldes</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statsData.balance_distribution || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}€`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(statsData.balance_distribution || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.status] || '#B8B8BA'} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1A1A1D', border: '1px solid #3A3A3D' }}
                  labelStyle={{ color: '#E8E8E8' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Graphique: Ventes vs Accès validés */}
          <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border">
            <h2 className="text-2xl font-bold text-white mb-6">Ventes vs Validations</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statsData.sales_vs_validations || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3A3A3D" />
                <XAxis dataKey="name" stroke="#B8B8BA" />
                <YAxis stroke="#B8B8BA" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1A1A1D', border: '1px solid #3A3A3D' }}
                  labelStyle={{ color: '#E8E8E8' }}
                />
                <Legend />
                <Bar dataKey="ventes" fill="#00FFFF" name="Ventes" />
                <Bar dataKey="validées" fill="#00FF88" name="Validées" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Graphique: Courbe des commissions mensuelles */}
        <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Évolution des commissions</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={statsData.monthly_commissions || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#3A3A3D" />
              <XAxis dataKey="month" stroke="#B8B8BA" />
              <YAxis stroke="#B8B8BA" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1A1A1D', border: '1px solid #3A3A3D' }}
                labelStyle={{ color: '#E8E8E8' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="disponible" 
                stroke="#00FF88" 
                strokeWidth={2}
                name="Disponible"
              />
              <Line 
                type="monotone" 
                dataKey="en_attente" 
                stroke="#FFB800" 
                strokeWidth={2}
                name="En attente"
              />
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="#00FFFF" 
                strokeWidth={2}
                name="Total"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default VendorStatistics;
